// CO / PO → Foundation staging tables through the bridge: Preview (no bridge write) → Dry run (bridge runs the
// INSERTs and ALWAYS rolls back) → Insert now (one document; typed confirmation + a fresh accepted dry run on these exact rows).
import { useState } from "react";
import { AlertTriangle, Eye, FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { foundationSyncDryRun, foundationSyncDryRuns, foundationSyncInsertNow, foundationSyncPreview, reportFoundationSyncError } from "@/lib/api";
import { FoundationStagingDialog } from "./FoundationStagingDialog";

const btn = "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border disabled:opacity-50";

export function FoundationDryRunButton({ woId, kind, docId, testId, onPushed, only }) {
  const [busy, setBusy] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [report, setReport] = useState(false);
  const previewOnly = only === "preview";

  const run = async (mode, confirm) => {
    setBusy(mode);
    try {
      if (mode === "preview") {
        const { data } = await foundationSyncPreview(woId, kind, docId);
        const accepted = data.payload_hash ? (await foundationSyncDryRuns(woId, kind, docId, data.payload_hash)).data.accepted : false;
        setDialog({ mode, data, accepted });
      } else if (mode === "dry_run") {
        const { data } = await foundationSyncDryRun(woId, kind, docId);
        setDialog({ mode, data, accepted: !!data.accepted });
        if (data.blocked) toast.error(`Blocked — ${data.error}`);
        else if (data.accepted) toast.success("Dry run accepted — rolled back, nothing written");
        else toast.error(data.nothing_sent ? "Could not reach Foundation — nothing was sent" : `Rejected — ${data.error || "see details"}`);
      } else {
        const { data } = await foundationSyncInsertNow(woId, kind, docId, confirm);
        setDialog({ mode, data, accepted: true });
        toast[data.status === "pushed" ? "success" : "error"](data.message || data.status);
        if (data.status === "pushed") onPushed?.(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${mode.replace("_", " ")} failed`);
    } finally { setBusy(null); }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testId}>
      <button type="button" onClick={() => run("preview")} disabled={!!busy} className={`${btn} border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800`} data-testid={`${testId}-preview`}>
        {busy === "preview" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Preview
      </button>
      {!previewOnly && (
        <button type="button" onClick={() => run("dry_run")} disabled={!!busy} className={`${btn} border-sky-300 dark:border-sky-700 text-sky-800 dark:text-sky-200 hover:bg-sky-50 dark:hover:bg-sky-950`} data-testid={`${testId}-dry-run`}>
          {busy === "dry_run" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />} Dry run
        </button>
      )}
      {!previewOnly && (
        <button type="button" onClick={() => setReport(true)} className={`${btn} border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-200 hover:bg-rose-50 dark:hover:bg-rose-950`} data-testid={`${testId}-report-issue`}>
          <AlertTriangle className="w-2.5 h-2.5" /> Report sync error
        </button>
      )}
      {report && <ReportSyncError woId={woId} kind={kind} docId={docId} onClose={() => setReport(false)} />}
      {dialog && (
        <FoundationStagingDialog kind={kind} mode={dialog.mode} data={dialog.data} accepted={dialog.accepted} busy={busy} onClose={() => setDialog(null)}
          onDryRun={() => run("dry_run")} onInsertNow={(confirm) => run("insert_now", confirm)} allowInsert={!previewOnly} testId={`${testId}-dialog`} />
      )}
    </div>
  );
}

export function ReportSyncError({ woId, kind, docId, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const send = async () => {
    if (!text.trim()) { toast.error("Paste the Foundation text first"); return; }
    setBusy(true);
    try {
      const r = await reportFoundationSyncError(woId, kind, docId, text);
      setSaved(r.data); setText(""); toast.success("Sync error stored against the latest attempt");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not store the error"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="report-sync-error">
        <DialogHeader>
          <DialogTitle className="text-sm">Report a Sync Error</DialogTitle>
          <DialogDescription className="text-xs">Paste Foundation's exact on-screen text (PO Sync / J/C Change Order Sync). It is stored against the latest attempt for this document.</DialogDescription>
        </DialogHeader>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={4} placeholder="e.g. Vendor not found / Cost class is required…"
          className="w-full text-xs font-mono rounded border border-rose-200 dark:border-rose-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 p-1.5" data-testid="report-sync-error-text" />
        <div className="flex items-center gap-2">
          <button type="button" onClick={send} disabled={busy} className="text-xs px-2.5 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50" data-testid="report-sync-error-send">Store</button>
          {saved && <span className="text-[11px] text-rose-800 dark:text-rose-200" data-testid="report-sync-error-saved">Stored {saved.at?.slice(0, 16).replace("T", " ")}</span>}
          <button type="button" onClick={onClose} className="ml-auto text-xs px-2.5 py-1 rounded border border-slate-300 dark:border-slate-600" data-testid="report-sync-error-close">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FoundationDryRunButton;
